import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Copy, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'

import { consulta, ErrorDeLaApi, pedir } from '../api/nucleo'
import { Aviso, Boton, Esqueleto, Modal, Pastilla, Pestanas, Tarjeta, clases } from '../componentes/ui'
import { registrarCambios } from './cambiosSinGuardar'
import { CampoGenerico } from './Campo'
import { CABECERA_EDITADO_DESDE, conflictoDe, diferencias, type Conflicto, type Diferencia } from './conflicto'
import { pestanasConErrores, primeraConErrores } from './errores'
import { aplanar, useEsquema, valorInicial, type Campo, type Esquema } from './esquema'
import { nuevoDe } from './genero'
import { Formulario, type Errores } from './Formulario'
import { GuardaDeNavegacion } from './GuardaDeNavegacion'
import { fechaLarga, Historial } from './Historial'
import { unir } from './rutas'
import { BotonVerEnLaWeb, BotonVistaPrevia, DireccionEnLaWeb } from './VerEnLaWeb'
import { erroresDeProblemas } from '../estructura/reglasDeVinculos'
import { type AlcanceDePrevia, COLECCIONES_CON_PREVIA } from '../api/previas'

/**
 * El editor genérico de un documento. Pinta lo que dice el esquema —lo principal en una
 * tarjeta ancha, lo lateral en tarjetas estrechas, las pestañas si las hay— y guarda por
 * la API con la sesión del usuario. Los errores de validación del núcleo se ponen
 * **junto a su campo**, con el mensaje que escribió el núcleo, y la pestaña que los tiene
 * se marca.
 *
 * Con borradores (páginas, entradas, productos): «Guardar borrador» guarda sin publicar,
 * «Publicar» publica, y la pestaña «Historial» enseña las versiones y restaura. Sin
 * borradores: «Guardar».
 *
 * Nada se pierde sin avisar (auditoría del 29-09-2026):
 * - salir con cambios sin guardar pregunta antes (`GuardaDeNavegacion`);
 * - cada guardado dice desde qué versión se editaba, y si otra persona guardó mientras
 *   tanto el núcleo responde 409 y aquí se ofrece ver sus cambios, guardar encima o
 *   recargar (§18).
 *
 * Los módulos pueden añadir pestañas propias (SEO con su análisis, campos del cliente)
 * por `pestanasExtra`, que reciben el estado del formulario.
 */
type Valor = Record<string, unknown>
type Modo = 'borrador' | 'guardar' | 'publicar'

/** Las colecciones cuyo estado cuenta la pantalla de Diseño antes de la primera publicación (`paginas/primeraPublicacion.ts`). */
const TOCAN_EL_ESTADO_DEL_DISENO = new Set(['paginas', 'productos'])

export type PestanaExtra = { clave: string; etiqueta: string; contenido: (contexto: { coleccion: string; id: null | number; valores: Valor }) => ReactNode; campos?: string[] }

/**
 * Lo que el editor le da a una **disposición propia** (el constructor de formularios): el
 * mismo documento, el mismo guardado —con su cabecera de concurrencia y su 409—, la misma
 * guarda de cambios sin guardar y los mismos errores del núcleo. Solo cambia cómo se ordena
 * en pantalla. Se pinta dentro del `<Formulario>`, así que los controles del motor
 * (`CampoGenerico`) funcionan igual.
 */
export type Edicion = {
  esquema: Esquema
  id: null | number
  inicial: Valor
  /** Lo último escrito: lo que se guardaría ahora. */
  valores: Valor
  sucio: boolean
  guardando: boolean
  errores: Errores
  mensaje: null | { texto: string; tono: 'aviso' | 'error' | 'ok' }
  cerrarMensaje: () => void
  /** Guarda sin publicar (las colecciones con borradores, como borrador). `true` si quedó guardado. */
  guardar: () => Promise<boolean>
  borrar: () => Promise<void>
  duplicar: () => Promise<void>
  puedeBorrar: boolean
  puedeDuplicar: boolean
}

type Props = {
  coleccion: string
  id: null | number
  sitio: null | number
  espacio: number
  base: string
  pestanasExtra?: PestanaExtra[]
  /** Campos de primer nivel que no se pintan en el cuerpo (los pinta una pestaña propia). */
  ocultarCampos?: string[]
  alGuardar?: (doc: Valor) => void
  /** Sin `sitio` en la colección (usuarios, por ejemplo). */
  sinSitio?: boolean
  /** Sin `cliente` ni `sitio`: ni se mandan ni se esperan (la colección los pone, o no existen). */
  sinPertenencia?: boolean
  puedeBorrar?: boolean
  /** Solo mirar: sin botones de guardar. Para lo que escribe solo el servidor (versiones, auditoría). */
  soloLectura?: boolean
  /** Campos que no están en el esquema y hay que pedir igual (la contraseña de un usuario nuevo). */
  camposExtra?: Campo[]
  /** Valores con los que nace un documento nuevo, además de los del esquema. */
  inicialExtra?: Valor
  /** Qué campo hace de título en la cabecera, si no es el de la colección (la auditoría enseña su frase, no su clave). */
  campoTitulo?: string
  /** Algo más en la cabecera (un botón de reembolsar, un enlace a la previa). `sucio`: hay cambios sin guardar. */
  acciones?: (contexto: { id: null | number; valores: Valor; recargar: () => void; sucio: boolean }) => ReactNode
  /** Debajo del título: un resumen propio del documento (las líneas de un pedido, los valores de un envío). */
  cabecera?: (contexto: { id: null | number; valores: Valor }) => ReactNode
  /**
   * Crear por otra puerta que `POST /<colección>` (un usuario se **invita**: `/panel/usuarios/invitar`).
   * Responde `{ doc, mensaje? }`; el mensaje del núcleo se enseña al llegar a la ficha.
   */
  rutaDeCreacion?: string
  /** El botón de crear, si no es «Guardar» («Invitar»). */
  textoDeCrear?: string
  /**
   * Antes de guardar: si devuelve un texto, se pregunta con él y solo se guarda si se
   * acepta. Para lo que tiene consecuencias que el formulario no enseña (cancelar un pedido
   * devuelve existencias y no devuelve el dinero).
   */
  antesDeGuardar?: (contexto: { antes: Valor; despues: Valor }) => null | string
  /**
   * Otra forma de ordenar el editor (el constructor de formularios): sustituye a la cabecera,
   * las pestañas y las tarjetas. El guardado, la guarda de navegación y el diálogo de
   * conflicto siguen siendo estos.
   */
  disposicion?: (edicion: Edicion) => ReactNode
  /** Cómo se llama lo que se edita, si no es lo que dice el esquema (un contenido de «Propiedades» es una «Propiedad»). */
  etiquetas?: { singular: string; plural: string }
  /**
   * La vista previa de lo guardado sin publicar, si no es la del documento en su dirección
   * (que sale sola en páginas, entradas, productos, servicios y contenidos): un tipo de
   * contenido o una taxonomía se previsualizan con su plantilla (`alcance: 'tipo'`).
   */
  previa?: null | AlcanceDePrevia
}

const quitarClaves = (valor: unknown): unknown => {
  if (Array.isArray(valor)) return valor.map(quitarClaves)
  if (valor && typeof valor === 'object') {
    const salida: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(valor as Record<string, unknown>)) if (k !== '_clave') salida[k] = quitarClaves(v)
    return salida
  }
  return valor
}

/**
 * Borra. Si el núcleo pide confirmación para algo que tiene consecuencias (un tipo de contenido
 * sin contenidos, un medio que se ve en la web: 409 con `confirmar: true`), se pregunta con su
 * mensaje y se repite con `?confirmar=si`. `false`: no se ha borrado porque no se ha querido.
 */
const borrarConfirmando = async (ruta: string): Promise<boolean> => {
  try {
    await pedir(ruta, { metodo: 'DELETE' })
    return true
  } catch (e) {
    if (!(e instanceof ErrorDeLaApi) || e.status !== 409 || e.datos.confirmar !== true) throw e
    if (!window.confirm(e.message)) return false
    await pedir(`${ruta}${consulta({ confirmar: 'si' })}`, { metodo: 'DELETE' })
    return true
  }
}

/** De un error de validación de Payload a un mapa ruta → mensaje. */
const erroresDe = (e: unknown): { errores: Errores; mensaje: string } => {
  if (!(e instanceof ErrorDeLaApi)) return { errores: {}, mensaje: 'No se ha podido guardar. Vuelve a intentarlo.' }
  const lista = Array.isArray(e.datos.errors) ? (e.datos.errors as Array<{ path?: string; field?: string; message?: string }>) : []
  // Un vínculo de una plantilla que no vale llega como `problemas: [{ clave, mensaje }]` (estructura/reglasDeVinculos.ts): va junto a su bloque.
  const errores: Errores = erroresDeProblemas(e.datos)
  for (const f of lista) {
    const ruta = f.path ?? f.field
    if (ruta && f.message) errores[ruta] = f.message
  }
  const cuantos = Object.keys(errores).length
  return { errores, mensaje: cuantos ? `Revisa ${cuantos === 1 ? 'el campo marcado' : `los ${cuantos} campos marcados`}.` : e.message }
}

/** El editor se rehace entero al cambiar de documento: nada del anterior (lo escrito, su versión) puede colarse en el siguiente. */
export const Editor = (p: Props) => <EditorDeDocumento key={`${p.coleccion}-${p.id ?? 'nuevo'}`} {...p} />

const EditorDeDocumento = (p: Props) => {
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const { data: esquema, isPending: cargandoEsquema } = useEsquema(p.coleccion)
  const { data: doc, isPending: cargandoDoc, error: errorDoc } = useQuery({
    queryKey: ['doc', p.coleccion, p.id],
    enabled: p.id !== null && Boolean(esquema),
    queryFn: () => pedir<Valor>(`/${p.coleccion}/${p.id}${consulta({ depth: 0, draft: esquema?.borradores ? true : undefined })}`),
  })

  const [errores, setErrores] = useState<Errores>({})
  const [mensaje, setMensaje] = useState<null | { texto: string; tono: 'aviso' | 'error' | 'ok' }>(null)
  const [guardando, setGuardando] = useState<null | Modo>(null)
  const [sucio, setSucio] = useState(false)
  // Lo que devuelve el núcleo al guardar: de ahí salen los campos que calcula el servidor
  // (los avisos del robots.txt, la puntuación de SEO), que el formulario enseña sin recargar.
  const [delServidor, setDelServidor] = useState<Valor | null>(null)
  const [pestana, setPestana] = useState('contenido')
  const [ultimos, setUltimos] = useState<Valor | null>(null)
  /** La versión desde la que se edita (`updatedAt`): la del documento abierto, o la del último guardado. */
  const [editadoDesde, setEditadoDesde] = useState<string | undefined>(undefined)
  /** El documento tal como estaba al empezar a editar: contra él se enseña qué cambió otra persona. */
  const [referencia, setReferencia] = useState<Valor | null>(null)
  /** Sube al recargar: el formulario se rehace con lo guardado. */
  const [generacion, setGeneracion] = useState(0)
  const [sincronia, setSincronia] = useState<{ marca: number; valores: Valor } | undefined>(undefined)
  const [conflicto, setConflicto] = useState<null | (Conflicto & { modo: Modo })>(null)

  // Lo que la guarda de navegación pregunta en el momento, sin esperar a un render.
  const sucioRef = useRef(false)
  const revision = useRef(0)

  const inicial = useMemo<Valor | null>(() => {
    if (!esquema) return null
    if (p.id !== null) return doc ?? null
    return { ...valorInicial(esquema.campos), ...(p.inicialExtra ?? {}) }
  }, [esquema, doc, p.id, p.inicialExtra])

  const desde = editadoDesde ?? (typeof inicial?.updatedAt === 'string' ? inicial.updatedAt : undefined)

  const marcarSucio = (valor: boolean) => {
    sucioRef.current = valor
    setSucio(valor)
  }

  useEffect(() => {
    if (!sucio) return
    const avisar = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', avisar)
    return () => window.removeEventListener('beforeunload', avisar)
  }, [sucio])

  // Lo que la guarda usa para «Guardar» desde su diálogo: el último `guardar`, con el modo que no publica nada.
  const guardarRef = useRef<(modo: Modo) => Promise<boolean>>(async () => false)
  const modoSinPublicar: Modo = esquema?.borradores ? 'borrador' : 'guardar'
  const modoRef = useRef<Modo>(modoSinPublicar)
  modoRef.current = modoSinPublicar
  useEffect(() => registrarCambios({ hayCambios: () => sucioRef.current, guardar: () => guardarRef.current(modoRef.current) }), [])

  /** Vuelve a leer el documento y rehace el formulario con él: lo escrito sin guardar se descarta. */
  const recargar = async (texto?: string) => {
    if (p.id === null) return
    // La consulta del documento está activa: invalidarla la vuelve a pedir, y se espera a tenerla.
    await clienteDeConsultas.invalidateQueries({ queryKey: ['doc', p.coleccion, p.id] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['historial', p.coleccion, p.id] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['seo-analisis', p.coleccion, p.id] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', p.coleccion] })
    marcarSucio(false)
    setUltimos(null)
    setEditadoDesde(undefined)
    setReferencia(null)
    setErrores({})
    setConflicto(null)
    setGeneracion((g) => g + 1)
    setMensaje(texto ? { texto, tono: 'ok' } : null)
  }

  if (cargandoEsquema || (p.id !== null && cargandoDoc)) return <Esqueleto filas={8} />
  if (!esquema) return <Aviso tono="error">No se ha podido leer la definición de esta sección.</Aviso>
  if (errorDoc) return <Aviso tono="error">Este documento no existe, o no es tuyo.</Aviso>
  if (!inicial) return <Esqueleto filas={8} />

  const ocultar = new Set([...(p.ocultarCampos ?? []), '_status', ...(p.pestanasExtra ?? []).flatMap((t) => t.campos ?? [])])
  // Un plegable o una fila sin nombre se esconde si todo lo que lleva dentro está escondido: si no, quedaría una caja vacía.
  const oculto = (c: Campo): boolean =>
    c.nombre ? ocultar.has(c.nombre) : (c.tipo === 'collapsible' || c.tipo === 'row') && (c.campos ?? []).length > 0 && (c.campos ?? []).every(oculto)
  const visibles = esquema.campos.filter((c) => !oculto(c))
  const principales = visibles.filter((c) => !c.lateral)
  const laterales = visibles.filter((c) => c.lateral)
  const actuales = ultimos ?? inicial
  const estado = String(actuales._status ?? '')
  // Un solo campo de pestañas de primer nivel (los ajustes) ocupa el ancho entero, sin tarjeta alrededor.
  const soloPestanas = principales.length === 1 && principales[0]?.tipo === 'tabs' && laterales.length === 0

  const conHistorial = esquema.borradores && p.id !== null
  const pestanasPropias = [...(p.pestanasExtra ?? []).map((t) => ({ clave: t.clave, etiqueta: t.etiqueta, campos: t.campos }))]
  const conErrores = pestanasConErrores(errores, pestanasPropias)

  /** Guarda. `encima`: la marca de la versión que otra persona guardó, para guardar encima a sabiendas. Devuelve si quedó guardado. */
  const guardar = async (modo: Modo, o: { encima?: string } = {}): Promise<boolean> => {
    const valores = quitarClaves(ultimos ?? inicial) as Valor
    const pregunta = p.antesDeGuardar?.({ antes: inicial, despues: valores })
    if (pregunta && !window.confirm(pregunta)) return false
    const revisionAlEmpezar = revision.current
    setGuardando(modo)
    setMensaje(null)
    setErrores({})
    try {
      const cuerpo: Valor = { ...valores }
      delete cuerpo.id
      delete cuerpo.createdAt
      delete cuerpo.updatedAt
      if (p.id === null && !p.sinPertenencia) {
        cuerpo.cliente = p.espacio
        if (p.sitio && !p.sinSitio) cuerpo.sitio = p.sitio
      }
      if (esquema.borradores) cuerpo._status = modo === 'publicar' ? 'published' : 'draft'
      const query = consulta({ depth: 0, draft: esquema.borradores && modo === 'borrador' ? true : undefined })
      const marca = o.encima ?? desde
      const respuesta =
        p.id === null
          ? await pedir<{ doc: Valor; mensaje?: string }>(p.rutaDeCreacion ?? `/${p.coleccion}${query}`, { metodo: 'POST', cuerpo })
          : await pedir<{ doc: Valor; mensaje?: string }>(`/${p.coleccion}/${p.id}${query}`, { metodo: 'PATCH', cuerpo, cabeceras: marca ? { [CABECERA_EDITADO_DESDE]: marca } : undefined })
      // Lo que calcula el servidor (los campos de solo lectura) llega al formulario aunque se siga escribiendo.
      setDelServidor(respuesta.doc)
      void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', p.coleccion] })
      // Lo que cuenta Diseño (qué está publicado, si la portada lo está) cambia con las páginas y los productos.
      if (TOCAN_EL_ESTADO_DEL_DISENO.has(p.coleccion)) void clienteDeConsultas.invalidateQueries({ queryKey: ['diseno-estado'] })
      p.alGuardar?.(respuesta.doc)
      if (p.id === null) {
        marcarSucio(false)
        // El editor sigue montado al pasar a la ficha nueva: el mensaje del núcleo llega con ella.
        if (respuesta.mensaje) setMensaje({ texto: respuesta.mensaje, tono: 'ok' })
        navegar(`${p.base}/${respuesta.doc.id}`, { replace: true })
        return true
      }
      clienteDeConsultas.setQueryData(['doc', p.coleccion, p.id], respuesta.doc)
      void clienteDeConsultas.invalidateQueries({ queryKey: ['historial', p.coleccion, p.id] })
      // La puntuación de SEO la calcula el núcleo al guardar: la pestaña vuelve a pedirla.
      void clienteDeConsultas.invalidateQueries({ queryKey: ['seo-analisis', p.coleccion, p.id] })
      setEditadoDesde(typeof respuesta.doc.updatedAt === 'string' ? respuesta.doc.updatedAt : undefined)
      setReferencia(respuesta.doc)
      setConflicto(null)
      // Si mientras se guardaba no se ha tocado nada, el formulario pasa a ser lo guardado (con lo
      // que calculó el núcleo). Si se ha seguido escribiendo, eso sigue ahí, sin guardar.
      if (revision.current === revisionAlEmpezar) {
        setUltimos(respuesta.doc)
        setSincronia({ marca: Date.now(), valores: respuesta.doc })
        marcarSucio(false)
      }
      setMensaje({ texto: modo === 'publicar' ? 'Publicado.' : modo === 'borrador' ? 'Borrador guardado.' : 'Guardado.', tono: 'ok' })
      return true
    } catch (e) {
      const choque = conflictoDe(e)
      if (choque) {
        setConflicto({ ...choque, modo })
        setMensaje({ texto: 'No se ha guardado: alguien ha guardado cambios mientras editabas.', tono: 'aviso' })
        return false
      }
      const { errores: err, mensaje: texto } = erroresDe(e)
      setErrores(err)
      setMensaje({ texto, tono: 'error' })
      // Los errores pueden estar en otra pestaña (el SEO): se lleva a la primera que los tenga.
      const tienen = pestanasConErrores(err, pestanasPropias)
      const primera = primeraConErrores(['contenido', ...pestanasPropias.map((t) => t.clave)], tienen)
      if (primera && !tienen.has(pestana)) setPestana(primera)
      return false
    } finally {
      setGuardando(null)
    }
  }
  guardarRef.current = guardar

  const borrar = async () => {
    if (p.id === null || !window.confirm(`¿Borrar «${tituloDe(actuales, esquema, p.campoTitulo) || 'este documento'}»? No se puede deshacer.`)) return
    try {
      if (!(await borrarConfirmando(`/${p.coleccion}/${p.id}`))) return
      void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', p.coleccion] })
      if (TOCAN_EL_ESTADO_DEL_DISENO.has(p.coleccion)) void clienteDeConsultas.invalidateQueries({ queryKey: ['diseno-estado'] })
      // Lo borrado no tiene cambios que guardar: se sale sin preguntar.
      marcarSucio(false)
      navegar(p.base)
    } catch (e) {
      setMensaje({ texto: e instanceof Error ? e.message : 'No se ha podido borrar.', tono: 'error' })
    }
  }

  const duplicar = async () => {
    if (p.id === null) return
    try {
      const r = await pedir<{ doc: Valor }>(`/${p.coleccion}/${p.id}/duplicate`, { metodo: 'POST', cuerpo: {} })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', p.coleccion] })
      if (TOCAN_EL_ESTADO_DEL_DISENO.has(p.coleccion)) void clienteDeConsultas.invalidateQueries({ queryKey: ['diseno-estado'] })
      navegar(`${p.base}/${r.doc.id}`)
    } catch (e) {
      setMensaje({ texto: e instanceof Error ? e.message : 'No se ha podido duplicar.', tono: 'error' })
    }
  }

  const pestanas = [
    { clave: 'contenido', etiqueta: 'Contenido', aviso: conErrores.has('contenido') },
    ...pestanasPropias.map((t) => ({ clave: t.clave, etiqueta: t.etiqueta, aviso: conErrores.has(t.clave) })),
    ...(conHistorial ? [{ clave: 'historial', etiqueta: 'Historial' }] : []),
  ]
  const titulo = tituloDe(actuales, esquema, p.campoTitulo)
  const etiqueta = p.etiquetas ?? esquema.etiqueta
  const singular = etiqueta.singular.toLowerCase()
  const duplicable = esquema.duplicable !== false
  // «Ver en la web ↗»: solo en lo que tiene dirección en la web, ya guardado y de una web.
  const enLaWeb = esquema.enLaWeb && p.id !== null && p.sitio !== null && !p.sinSitio ? { coleccion: p.coleccion, sitio: p.sitio, id: p.id, borradores: esquema.borradores, estado, sucio } : null
  // La vista previa: la propia de la pantalla (un tipo, una taxonomía) o la del documento en su dirección, si lo admite el núcleo.
  const previa: AlcanceDePrevia | null =
    !esquema.borradores || p.id === null || p.sitio === null || p.sinSitio
      ? null
      : p.previa !== undefined
        ? p.previa
        : COLECCIONES_CON_PREVIA.has(p.coleccion)
          ? { alcance: 'documento', documento: { coleccion: p.coleccion, id: p.id } }
          : null

  const dialogoDeConflicto = conflicto && (
    <DialogoDeConflicto
      alCerrar={() => setConflicto(null)}
      alGuardarEncima={() => void guardar(conflicto.modo, { encima: conflicto.modificadoEn })}
      alRecargar={() => void recargar('Cargado lo último que hay guardado. Tus cambios sin guardar se han descartado.')}
      antes={referencia ?? inicial}
      coleccion={p.coleccion}
      conflicto={conflicto}
      esquema={esquema}
      guardando={guardando !== null}
      id={p.id}
    />
  )

  if (p.disposicion) {
    const edicion: Edicion = {
      esquema,
      id: p.id,
      inicial,
      valores: actuales,
      sucio,
      guardando: guardando !== null,
      errores,
      mensaje,
      cerrarMensaje: () => setMensaje(null),
      guardar: () => guardar(modoSinPublicar),
      borrar,
      duplicar,
      puedeBorrar: p.id !== null && !p.soloLectura && p.puedeBorrar !== false && esquema.puede?.borrar !== false,
      puedeDuplicar: p.id !== null && !p.soloLectura && duplicable && esquema.puede?.duplicar !== false,
    }
    return (
      <Formulario
        alCambiar={(v) => {
          revision.current += 1
          setUltimos(v)
          marcarSucio(true)
        }}
        delServidor={delServidor}
        errores={errores}
        espacio={p.espacio}
        esquema={esquema}
        inicial={inicial}
        key={generacion}
        sincronizar={sincronia}
        sitio={p.sitio}
        soloLectura={p.soloLectura}
      >
        {!p.soloLectura && <GuardaDeNavegacion />}
        {p.disposicion(edicion)}
        {dialogoDeConflicto}
      </Formulario>
    )
  }

  return (
    <Formulario
      alCambiar={(v) => {
        revision.current += 1
        setUltimos(v)
        marcarSucio(true)
      }}
      delServidor={delServidor}
      errores={errores}
      espacio={p.espacio}
      esquema={esquema}
      inicial={inicial}
      key={generacion}
      sincronizar={sincronia}
      sitio={p.sitio}
      soloLectura={p.soloLectura}
    >
      {!p.soloLectura && <GuardaDeNavegacion />}
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Link className="mb-2 inline-flex items-center gap-1 text-[13px] text-tenue hover:text-tinta" to={p.base}>
            <ArrowLeft className="size-3.5" />
            {etiqueta.plural}
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="truncate font-display text-[26px] leading-tight font-semibold tracking-tight">
              {p.id === null ? nuevoDe(singular) : titulo || etiqueta.singular}
            </h1>
            {esquema.borradores && p.id !== null && <Pastilla clave={estado === 'published' ? 'publicado' : 'sin-publicar'}>{estado === 'published' ? 'Publicado' : 'Borrador'}</Pastilla>}
            {/* La portada la dice el núcleo (`esPortada`): aquí no se repite la regla. */}
            {actuales.esPortada === true && (
              <span title="Es la página que se abre en la dirección principal de tu web.">
                <Pastilla clave="violeta" punto={false}>
                  Portada
                </Pastilla>
              </span>
            )}
            {sucio && <span className="text-xs text-tenue">Cambios sin guardar</span>}
          </div>
          {/* La dirección en la web, como el enlace permanente de WordPress: la da el núcleo, con la misma regla que la web. */}
          {enLaWeb && <DireccionEnLaWeb {...enLaWeb} />}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {enLaWeb && <BotonVerEnLaWeb {...enLaWeb} />}
          {p.acciones?.({ id: p.id, valores: actuales, recargar: () => void recargar(), sucio })}
          {/* Solo lo que el núcleo va a dejar hacer a quien mira: un editor no ve «Borrar» en un envío, ni nadie «Duplicar» en una pasarela. */}
          {p.id !== null && !p.soloLectura && p.puedeBorrar !== false && (
            <>
              {esquema.puede?.borrar !== false && (
                <Boton icono={<Trash2 />} onClick={borrar} pequeno tipo="peligro">
                  Borrar
                </Boton>
              )}
              {duplicable && esquema.puede?.duplicar !== false && (
                <Boton disabled={sucio} icono={<Copy />} onClick={duplicar} pequeno tipo="discreto" title={sucio ? 'Guarda antes de duplicar: la copia se hace de lo guardado.' : undefined}>
                  Duplicar
                </Boton>
              )}
            </>
          )}
          {/* «Vista previa» de lo guardado sin publicar (plan del CMS dinámico §15): en su dirección, con un enlace que caduca. */}
          {previa && p.sitio !== null && <BotonVistaPrevia que={previa} sitio={p.sitio} sucio={sucio} />}
          {p.soloLectura ? null : esquema.borradores ? (
            <>
              <Boton cargando={guardando === 'borrador'} disabled={guardando !== null} onClick={() => void guardar('borrador')}>
                Guardar borrador
              </Boton>
              <Boton cargando={guardando === 'publicar'} disabled={guardando !== null} onClick={() => void guardar('publicar')} tipo="primario">
                Publicar
              </Boton>
            </>
          ) : (
            <Boton cargando={guardando !== null} disabled={guardando !== null} onClick={() => void guardar('guardar')} tipo="primario">
              {p.id === null && p.textoDeCrear ? p.textoDeCrear : 'Guardar'}
            </Boton>
          )}
        </div>
      </header>

      {mensaje && (
        <div className="mb-5">
          <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso>
        </div>
      )}

      {p.cabecera && <div className="mb-6">{p.cabecera({ id: p.id, valores: actuales })}</div>}

      {pestanas.length > 1 && (
        <div className="mb-6">
          <Pestanas activa={pestana} alCambiar={setPestana} pestanas={pestanas} />
        </div>
      )}

      <div className={clases(pestana !== 'contenido' && 'hidden', !soloPestanas && 'grid gap-6 lg:grid-cols-[minmax(0,1fr)_19rem]')}>
        {soloPestanas ? (
          <Tarjeta>
            {principales.map((c, i) => (
              <CampoGenerico campo={c} key={c.nombre ?? i} ruta={unir('', c.nombre)} />
            ))}
          </Tarjeta>
        ) : (
          <>
            <Tarjeta className="flex min-w-0 flex-col gap-5">
              {(p.id === null ? (p.camposExtra ?? []) : []).map((c, i) => (
                <CampoGenerico campo={c} key={`extra-${c.nombre ?? i}`} ruta={unir('', c.nombre)} />
              ))}
              {principales.map((c, i) => (
                <CampoGenerico campo={c} key={c.nombre ?? i} ruta={unir('', c.nombre)} />
              ))}
              {principales.length === 0 && (p.camposExtra ?? []).length === 0 && <p className="text-sm text-tenue">Este documento no tiene campos que editar aquí.</p>}
            </Tarjeta>
            <aside className="flex flex-col gap-4">
              {laterales.length > 0 && (
                <Tarjeta className="flex flex-col gap-5">
                  {laterales.map((c, i) => (
                    <CampoGenerico campo={c} key={c.nombre ?? i} ruta={unir('', c.nombre)} />
                  ))}
                </Tarjeta>
              )}
              {p.id !== null && (
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 px-1 text-xs text-tenue">
                  {typeof actuales.updatedAt === 'string' && (
                    <>
                      <dt>Modificado</dt>
                      <dd className="text-tinta-2">
                        {new Date(actuales.updatedAt).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        {typeof actuales.modificadoPor === 'string' && actuales.modificadoPor ? ` · ${actuales.modificadoPor}` : ''}
                      </dd>
                    </>
                  )}
                  {typeof actuales.createdAt === 'string' && (
                    <>
                      <dt>Creado</dt>
                      <dd className="text-tinta-2">{new Date(actuales.createdAt).toLocaleString('es', { day: 'numeric', month: 'short', year: 'numeric' })}</dd>
                    </>
                  )}
                </dl>
              )}
            </aside>
          </>
        )}
      </div>

      {(p.pestanasExtra ?? []).map((t) => (
        <div className={pestana === t.clave ? '' : 'hidden'} key={t.clave}>
          {t.contenido({ coleccion: p.coleccion, id: p.id, valores: actuales })}
        </div>
      ))}

      {conHistorial && pestana === 'historial' && p.id !== null && (
        <Historial
          alFallar={(e) => {
            const choque = conflictoDe(e)
            if (choque) setConflicto({ ...choque, modo: modoSinPublicar })
            setMensaje({ texto: choque ? 'No se ha restaurado: alguien ha guardado cambios mientras tanto.' : e instanceof Error ? e.message : 'No se ha podido restaurar.', tono: choque ? 'aviso' : 'error' })
          }}
          alRestaurar={(fecha) => void recargar(`Restaurada la versión del ${fecha}. Está guardada como borrador: revísala y publica cuando quieras.`).then(() => setPestana('contenido'))}
          coleccion={p.coleccion}
          editadoDesde={desde}
          id={p.id}
          sucio={sucio}
          titulo={esquema.titulo}
        />
      )}

      {dialogoDeConflicto}
    </Formulario>
  )
}

const tituloDe = (valores: Valor, esquema: Esquema, campo?: string): string => {
  const v = valores[campo ?? esquema.titulo]
  return typeof v === 'string' && v ? v : ''
}

/**
 * «Alguien ha guardado cambios mientras editabas» (§18): nada se pisa en silencio. Se
 * puede ver qué cambió la otra persona (lo que había al abrir frente a lo guardado ahora),
 * guardar lo tuyo encima a sabiendas, o recargar y descartar lo tuyo.
 */
const DialogoDeConflicto = ({
  conflicto,
  coleccion,
  id,
  esquema,
  antes,
  guardando,
  alCerrar,
  alGuardarEncima,
  alRecargar,
}: {
  alCerrar: () => void
  alGuardarEncima: () => void
  alRecargar: () => void
  antes: Valor
  coleccion: string
  conflicto: Conflicto
  esquema: Esquema
  guardando: boolean
  id: null | number
}) => {
  const [cambios, setCambios] = useState<Diferencia[] | null>(null)
  const [leyendo, setLeyendo] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)

  const verCambios = async () => {
    if (id === null) return
    setLeyendo(true)
    setFallo(null)
    try {
      const ahora = await pedir<Valor>(`/${coleccion}/${id}${consulta({ depth: 0, draft: esquema.borradores ? true : undefined })}`)
      setCambios(diferencias(antes, ahora, esquema.campos))
    } catch (e) {
      setFallo(e instanceof Error ? e.message : 'No se ha podido leer lo guardado.')
    } finally {
      setLeyendo(false)
    }
  }

  const quien = conflicto.modificadoPor ?? 'Otra persona'
  return (
    <Modal
      ancho="max-w-2xl"
      cerrar={alCerrar}
      pie={
        <>
          <Boton disabled={guardando} onClick={alCerrar} tipo="discreto">
            Seguir editando
          </Boton>
          <Boton disabled={guardando} onClick={alRecargar}>
            Recargar lo suyo
          </Boton>
          <Boton cargando={guardando} onClick={alGuardarEncima} tipo="peligro">
            Guardar lo mío encima
          </Boton>
        </>
      }
      titulo="Alguien ha guardado cambios mientras editabas"
    >
      <div className="flex flex-col gap-4 text-sm text-tinta-2">
        <p>
          {quien} guardó este documento el {fechaLarga(conflicto.modificadoEn)}, después de que tú lo abrieras. No se ha guardado nada tuyo todavía.
        </p>
        <ul className="list-disc pl-5">
          <li>
            <strong>Guardar lo mío encima</strong>: lo que ves ahora sustituye a lo de {quien === 'Otra persona' ? 'la otra persona' : quien}, y sus cambios se pierden.
          </li>
          <li>
            <strong>Recargar lo suyo</strong>: ves lo último guardado y tus cambios se descartan.
          </li>
          <li>
            <strong>Seguir editando</strong>: no pasa nada; puedes copiar lo tuyo antes de decidir.
          </li>
        </ul>
        {cambios === null ? (
          <div>
            <Boton cargando={leyendo} disabled={id === null} onClick={() => void verCambios()} pequeno>
              Ver qué cambió
            </Boton>
          </div>
        ) : cambios.length === 0 ? (
          <Aviso tono="info">No cambió ningún campo que se edite aquí (puede que solo lo volvieran a guardar).</Aviso>
        ) : (
          <div className="overflow-x-auto rounded-caja border border-linea">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-linea bg-papel-2 text-left text-xs text-tenue">
                  <th className="px-3 py-2 font-medium">Campo</th>
                  <th className="px-3 py-2 font-medium">Cuando lo abriste</th>
                  <th className="px-3 py-2 font-medium">Ahora</th>
                </tr>
              </thead>
              <tbody>
                {cambios.map((c) => (
                  <tr className="border-b border-linea last:border-b-0" key={c.campo}>
                    <td className="px-3 py-2 font-medium text-tinta">{c.etiqueta}</td>
                    <td className="px-3 py-2">{c.antes}</td>
                    <td className="px-3 py-2">{c.ahora}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {fallo && <Aviso tono="error">{fallo}</Aviso>}
      </div>
    </Modal>
  )
}

/** Un campo de primer nivel del esquema, por nombre: lo usan las pestañas propias para pintar un grupo entero. */
export const campoDeNivelSuperior = (esquema: Esquema, nombre: string): Campo | undefined => aplanar(esquema.campos).find((c) => c.nombre === nombre)
